import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePageLayout } from '@app/router/index'
import { ROUTES } from '@app/router/routes'
import { getRegionList, getAreaList, getTransmissionBanner, getBehindScenesList, getJointExhibitionVideoList } from '../api/transmissionService'
import ExhibitionMap from '../components/ExhibitionMap'
import PanoramaViewer from '../components/PanoramaViewer'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { isYouTubeUrl, getYouTubeThumbnail, getYouTubeEmbedUrl } from '@shared/utils/videoUtils'
import { isFeatureAccessible, FEATURE_FLAGS } from '@shared/config/featureFlags'
import { getUserInfo } from '@shared/api/client'
import InteractiveZoneItem from '@shared/components/InteractiveZoneItem'

const ACCENT = { borderLeft: '6px solid #BC313D', paddingLeft: 20 }
const LINE = { borderLeft: '1px solid #6E625B' }
const LINE_PL = { ...LINE, paddingLeft: 20 }

export default function TransmissionListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  // mapRestore 只在剛從報導頁／展區介紹頁 navigate 過來時套用一次；讀出來後要立刻從 history state 清掉，
  // 否則會殘留在這個 history entry 上，導致使用者重新整理頁面時又被拉回全螢幕地圖、紅點跳動指示又重新出現，
  // 且第一層地圖的展區二熱區會卡在高亮狀態退不出來。
  // 用 render 階段同步呼叫 window.history.replaceState（而非 useEffect + navigate）立即清除，
  // 因為 useEffect／navigate 都要等一輪 render 週期才生效，使用者若在那之前重新整理就會來不及清掉；
  // React Router 的 browser history 把 location.state 存在 window.history.state.usr 底下
  const mapRestoreStateRef = useRef(location.state?.mapRestore ?? null)
  const mapRestoreState = mapRestoreStateRef.current
  const mapSectionRef = useRef(null)
  if (window.history.state?.usr?.mapRestore) {
    window.history.replaceState({ ...window.history.state, usr: null }, '')
  }
  const { t, currentLanguage } = useLanguage()
  const { setLayoutOptions } = usePageLayout()
  const isLoggedIn = !!getUserInfo()
  const isWishTreeEnabled = isFeatureAccessible(FEATURE_FLAGS.WISH_TREE, isLoggedIn)
  const isEn = currentLanguage.code === 'en'
  const pillFontSize = isEn ? 22 : 32
  const pillSpacing = isEn ? 3 : 7
  const [regions, setRegions] = useState([])
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [areas, setAreas] = useState([])
  const [selectedArea, setSelectedArea] = useState(null)
  const [groupMode, setGroupMode] = useState('area') // 'region' | 'area'
  const [isLoading, setIsLoading] = useState(true)
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768)
  const [bannerDetails, setBannerDetails] = useState(null)
  const [behindScenes, setBehindScenes] = useState([])
  const [jointExhibitionVideos, setJointExhibitionVideos] = useState([])
  const [selectedScene, setSelectedScene] = useState(null)
  // 展區資訊公開時間；在這個時間之前，互動地圖只能看第一層、所有熱區都不能點
  const [areaPublishedTime, setAreaPublishedTime] = useState(null)
  const isAreaPublished = !!areaPublishedTime && Date.now() >= new Date(areaPublishedTime).getTime()
  const areaPublishedDateStr = areaPublishedTime
    ? new Intl.DateTimeFormat('zh-TW', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(areaPublishedTime))
    : ''

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (mapRestoreState && !mapRestoreState.isFullscreen) {
      setTimeout(() => {
        mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }
  }, [mapRestoreState])

  useEffect(() => {
    setLayoutOptions({ noPaddingBottom: true })
    return () => setLayoutOptions({})
  }, [setLayoutOptions])

  useEffect(() => {
    setIsLoading(true)

    getRegionList()
      .then(res => {
        const items = res.items ?? []
        setRegions(items)
        if (items.length > 0) setSelectedRegion(items[0].name)
      })
      .catch(err => console.error('Failed to fetch region list:', err))
      .finally(() => setIsLoading(false))

    getAreaList()
      .then(res => {
        // area/list 回傳格式跟 region/list 不同：是扁平的 { area, monastery_items[] }
        // 這裡正規化成跟 region 一樣的 { name, children: [{ type: 'monastery', id, name, ... }] } 樹狀結構，方便沿用同一套渲染邏輯
        const items = (res.items ?? []).map(group => ({
          name: group.area,
          children: (group.monastery_items ?? []).map(m => ({
            type: 'monastery',
            id: m.monastery_id,
            name: m.monastery_name,
            address: m.address,
            has_exhibition_post: m.has_exhibition_post,
          })),
        }))
        setAreas(items)
        if (items.length > 0) setSelectedArea(items[0].name)
        setAreaPublishedTime(res.published_time ?? null)
      })
      .catch(err => console.error('Failed to fetch area list:', err))

    getTransmissionBanner()
      .then(res => setBannerDetails(res.media?.[0]?.details ?? null))
      .catch(err => console.error('Failed to fetch transmission banner:', err))

    getBehindScenesList()
      .then(res => setBehindScenes(res.items || []))
      .catch(err => console.error('Failed to fetch behind scenes:', err))

    getJointExhibitionVideoList()
      .then(res => setJointExhibitionVideos(res.items || []))
      .catch(err => console.error('Failed to fetch joint exhibition videos:', err))
  }, [currentLanguage])

  const handleMonasteryClick = (monastery) => {
    if (!monastery.has_exhibition_post) return
    navigate(ROUTES.TRANSMISSION_DETAIL(monastery.id), {
      state: { monasteryName: monastery.name }
    })
  }

  const flatMonasteries = (children) => {
    const result = []
      ; (children || []).forEach(child => {
        if (child.type === 'monastery') result.push(child)
        else if (child.children) result.push(...flatMonasteries(child.children))
      })
    return result
  }

  const MonasteryTag = ({ m }) => {
    const clickable = !!m.has_exhibition_post
    return (
      <div
        onClick={() => clickable && handleMonasteryClick(m)}
        style={{
          paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2,
          background: clickable ? 'rgba(0,70,130,0.75)' : '#a0a0a05e',
          boxShadow: clickable ? '0px 0px 4px 2px rgba(188,210,228,0.70) inset' : 'none',
          borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: clickable ? 'pointer' : 'default',
          minHeight: 60, width: '100%', height: '100%',
        }}
      >
        <span
          className="font-serif text-[16px] min-[400px]:text-[20px]"
          style={{
            textAlign: 'center',
            color: clickable ? '#fff' : '#1b1a1e96',
            fontWeight: 500,
            lineHeight: '30px',
          }}
        >
          {m.name}
        </span>
      </div>
    )
  }

  const MonasteryGrid = ({ list }) =>
    isDesktop ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {list.map(m => <MonasteryTag key={m.id} m={m} />)}
      </div>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {list.map(m => <MonasteryTag key={m.id} m={m} />)}
        {list.length % 2 === 1 && <div />}
      </div>
    )

  const activeGroups = groupMode === 'area' ? areas : regions
  const activeSelected = groupMode === 'area' ? selectedArea : selectedRegion
  const setActiveSelected = groupMode === 'area' ? setSelectedArea : setSelectedRegion

  const filteredRegions = activeSelected
    ? activeGroups.filter(r => r.name === activeSelected)
    : activeGroups

  return (
    <div style={{ backgroundColor: '#FEFEFE' }}>

      {/* ── Banner ── */}
      <div className="relative w-full">
        <img
          src="/image/transmission/banner_desktop.jpg"
          alt={t('transmission.pageTitle')}
          className="w-full h-auto block"
        />
      </div>

      {/* ── Banner 文字（水平置中，同首頁樣式） ── */}
      {bannerDetails && (
        <div className="relative w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto" style={{ zIndex: 1 }}>
          <section className="px-6 pt-10 pb-0 text-center flex flex-col items-center">
            {bannerDetails.subtitle && (
              <h2
                className="font-['Noto_Serif_TC']"
                style={{ color: '#171717', fontSize: 24, fontWeight: 500, lineHeight: '36px' }}
              >
                {bannerDetails.subtitle}
              </h2>
            )}
            {bannerDetails.content && (
              <p
                className="font-['Noto_Sans_TC'] whitespace-pre-line"
                style={{ color: '#6E625B', fontSize: 20, fontWeight: 400, lineHeight: '32px', letterSpacing: '0.38px', marginTop: 12 }}
              >
                {bannerDetails.content}
              </p>
            )}
          </section>
        </div>
      )}

      {/* ── 展場資訊 section ── */}
      <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto" ref={mapSectionRef}>
        <section className="px-6">
          <div className="h-10" />

          <div style={ACCENT}>
            <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
              <span style={{ color: '#171717' }}>{t('transmission.exhibitionInfoTitleBlack')}</span><br />
              <span style={{ color: '#BC313D' }}>{t('transmission.exhibitionInfoTitleRed')}</span>
            </h2>
          </div>

          <div style={{ ...LINE, paddingTop: 24, paddingBottom: 16 }}>
            <ExhibitionMap initialRestoreState={mapRestoreState} publishedTime={areaPublishedTime} />
          </div>

          {/* 360 全景 demo：暫時隱藏，先專注 2D 地圖 */}
          {false && (
            <div style={{ ...LINE, paddingLeft: 26, paddingTop: 16, paddingBottom: 16 }}>
              <PanoramaViewer />
            </div>
          )}

          <div className="h-6" style={LINE_PL} />
        </section>
      </div>

      {/* ── 聯展影片（空則不顯示） ── */}
      {jointExhibitionVideos.length > 0 && (
        <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
          <section className="px-6 relative">
            <div className="relative" style={{ zIndex: 1 }}>
              <div className="h-6" style={LINE_PL} />

              <div style={ACCENT}>
                <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
                  <span style={{ color: '#171717' }}>{t('transmission.jointExhibitionTitleBlack')}</span><br />
                  <span style={{ color: '#BC313D' }}>{t('transmission.jointExhibitionTitleRed')}</span>
                </h2>
              </div>

              <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>
                <div className="overflow-x-auto no-scrollbar" style={{ display: 'flex', gap: 12, marginRight: -24, paddingRight: 24 }}>
                  {jointExhibitionVideos.map((scene) => {
                    const isYT = scene.media_url && isYouTubeUrl(scene.media_url)
                    const thumb = isYT ? getYouTubeThumbnail(scene.media_url) : null
                    return (
                      <div
                        key={scene.behind_scenes_id}
                        className="cursor-pointer group"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 155, flexShrink: 0 }}
                        onClick={() => scene.media_url && setSelectedScene(scene)}
                      >
                        <div style={{ width: 155, aspectRatio: '1', position: 'relative', background: '#FAF7F3', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 16, overflow: 'hidden' }}>
                          {thumb ? (
                            <img src={thumb} alt={scene.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : scene.media_url ? (
                            <video src={`${scene.media_url}#t=0.1`} preload="metadata" muted playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(188,49,61,0.08)' }}>
                              <span style={{ fontFamily: 'Noto Serif TC', fontSize: 20, fontWeight: 700, color: 'rgba(188,49,61,0.35)' }}>展</span>
                            </div>
                          )}
                          {scene.media_url && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
                              <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 32 }}>play_arrow</span>
                            </div>
                          )}
                        </div>
                        <p className="line-clamp-2 text-center" style={{ fontFamily: 'Noto Serif TC', fontSize: 14, fontWeight: 700, color: '#6E625B', letterSpacing: '0.1em', width: '100%' }}>
                          {scene.title}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="h-6" style={LINE_PL} />
            </div>
          </section>
        </div>
      )}

      {/* ── 活動花絮（空則不顯示） ── */}
      {behindScenes.length > 0 && (
        <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
          <section className="px-6 relative">
            <div className="relative" style={{ zIndex: 1 }}>
              <div className="h-6" style={LINE_PL} />

              <div style={ACCENT}>
                <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
                  <span style={{ color: '#171717' }}>{t('transmission.highlightsTitleBlack')}</span><br />
                  <span style={{ color: '#BC313D' }}>{t('transmission.highlightsTitleRed')}</span>
                </h2>
              </div>

              <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>
                <div className="overflow-x-auto no-scrollbar" style={{ display: 'flex', gap: 12, marginRight: -24, paddingRight: 24 }}>
                  {behindScenes.map((scene) => {
                    const isYT = scene.media_url && isYouTubeUrl(scene.media_url)
                    const thumb = isYT ? getYouTubeThumbnail(scene.media_url) : null
                    return (
                      <div
                        key={scene.behind_scenes_id}
                        className="cursor-pointer group"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 155, flexShrink: 0 }}
                        onClick={() => scene.media_url && setSelectedScene(scene)}
                      >
                        <div style={{ width: 155, aspectRatio: '1', position: 'relative', background: '#FAF7F3', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 16, overflow: 'hidden' }}>
                          {thumb ? (
                            <img src={thumb} alt={scene.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : scene.media_url ? (
                            <video src={`${scene.media_url}#t=0.1`} preload="metadata" muted playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(188,49,61,0.08)' }}>
                              <span style={{ fontFamily: 'Noto Serif TC', fontSize: 20, fontWeight: 700, color: 'rgba(188,49,61,0.35)' }}>花</span>
                            </div>
                          )}
                          {scene.media_url && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
                              <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 32 }}>play_arrow</span>
                            </div>
                          )}
                        </div>
                        <p className="line-clamp-2 text-center" style={{ fontFamily: 'Noto Serif TC', fontSize: 14, fontWeight: 700, color: '#6E625B', letterSpacing: '0.1em', width: '100%' }}>
                          {scene.title}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="h-6" style={LINE_PL} />
            </div>
          </section>
        </div>
      )}

      {/* ── 各精舍心傳 section ── */}
      <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
        <section className="px-6">
          <div className="h-10" style={LINE_PL} />

          {/* 標題 */}
          <div style={ACCENT}>
            <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
              <span style={{ color: '#171717' }}>{t('transmission.monasteryTransmissionTitleBlack')}</span><br />
              <span style={{ color: '#BC313D' }}>{t('transmission.monasteryTransmissionTitleRed')}</span>
            </h2>
          </div>

          <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>

            {areaPublishedDateStr && !isAreaPublished && (
              <p style={{ fontFamily: 'Noto Sans TC', fontSize: 14, color: '#6E625B', marginBottom: 16 }}>
                {t('transmission.monasteryListPublishedDesc').replace('{{date}}', areaPublishedDateStr)}
              </p>
            )}

            {isLoading ? (
              <div className="flex justify-center py-12">
                <span className="material-symbols-outlined animate-spin text-4xl" style={{ color: '#BC313D' }}>
                  progress_activity
                </span>
              </div>
            ) : (
              <>
                {/* 依地區 / 依展區 切換 */}
                <div className="flex gap-6 mb-4" style={{ borderBottom: '1px solid rgba(110,98,91,0.30)' }}>
                  {[
                    { mode: 'area', label: t('transmission.groupByArea') },
                    { mode: 'region', label: t('transmission.groupByRegion') },
                  ].map(tab => {
                    const isOn = groupMode === tab.mode
                    return (
                      <button
                        key={tab.mode}
                        onClick={() => setGroupMode(tab.mode)}
                        className="font-serif"
                        style={{
                          paddingBottom: 8,
                          fontSize: 20,
                          fontWeight: isOn ? 700 : 400,
                          color: isOn ? '#BC313D' : '#6E625B',
                          borderBottom: isOn ? '2px solid #BC313D' : '2px solid transparent',
                          marginBottom: -1,
                        }}
                      >
                        {tab.label}
                      </button>
                    )
                  })}
                </div>

                {/* 地區／展區 Chips */}
                {activeGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {activeGroups.map(group => {
                      const isOn = activeSelected === group.name
                      return (
                        <button
                          key={group.name}
                          onClick={() => setActiveSelected(isOn ? null : group.name)}
                          className="font-sans"
                          style={{
                            paddingTop: 4, paddingBottom: 5,
                            paddingLeft: 8, paddingRight: 8,
                            borderRadius: 8,
                            outline: '1px solid #BC313D',
                            outlineOffset: -1,
                            background: isOn ? '#BC313D' : 'transparent',
                            fontSize: 18,
                            fontWeight: isOn ? 500 : 300,
                            color: isOn ? '#fff' : '#BC313D',
                            lineHeight: '27px',
                          }}
                        >
                          {group.name}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* 精舍列表 */}
                <div style={{ paddingTop: 16, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 0, marginLeft: -6 }}>
                  {filteredRegions.map((region, idx) => {
                    const hasSubRegions = region.children?.some(c => c.type === 'sub_region')
                    return (
                      <div key={region.name}>
                        {hasSubRegions ? (
                          <div style={{ paddingBottom: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {region.children.map(sub => (
                              <div key={sub.name}>
                                <div style={{ fontSize: 22, color: '#CF4551', fontFamily: 'Noto Serif TC', marginBottom: 8 }}>
                                  {sub.name}
                                </div>
                                <MonasteryGrid list={flatMonasteries(sub.children)} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ paddingBottom: 12 }}>
                            <MonasteryGrid list={flatMonasteries(region.children)} />
                          </div>
                        )}
                        {idx < filteredRegions.length - 1 && (
                          <div style={{ height: 0, outline: '1px solid rgba(110,98,91,0.30)', outlineOffset: -0.5, marginBottom: 12 }} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* ── 花絮 Modal ── */}
      {selectedScene?.media_url && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedScene(null)}
        >
          <button
            onClick={() => setSelectedScene(null)}
            className="fixed top-4 right-4 z-[10000] w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 text-white transition-all"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isYouTubeUrl(selectedScene.media_url) ? (
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedScene.media_url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedScene.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <video
                  src={selectedScene.media_url}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
            {selectedScene.monastery_id && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => navigate(ROUTES.TRANSMISSION_DETAIL(selectedScene.monastery_id))}
                  className="flex items-center gap-2 px-6 py-2 rounded-full text-white transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#004682', fontFamily: 'Noto Sans TC', fontSize: 15, fontWeight: 500 }}
                >
                  {t('transmission.goToMonastery')}
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
    </div>
  )
}
